import axios from 'axios';

import {
  getOriginPointFromOPData,
  getProjectFromProjectData,
  getSampleFromSampleData,
  getSamplePointsFromSPData,
  getScenarioFromScenarioData,
  getSegmentFromSegmentsData,
  getTestFromTestData,
} from './formattingUtils';

import floorPlansApi from 'Api/floorPlans';
import originPointsApi from 'Api/originpoints';
import projectsApi from 'Api/projects';
import samplePlansApi from 'Api/samplePlans';
import samplePointsApi from 'Api/samplePoints';
import scenariosApi from 'Api/scenarios';
import segmentsApi from 'Api/segments';

import { cropImageFromUrlPromisified } from 'Components/Floorplans/utils';

import {
  getDataFromResponse,
  getErrorMessageFromResponse,
  isValidResponse,
} from 'Utils';

const getDataForProjectConfiguration = async (
  projectId,
  delayBetweenTestsMs = 500,
  mounted = {},
  successOnlyOnCompleteData = true,
  source,
) => {
  const maxAttempts = 2;
  const globalIndicators = {
    shouldStop: false,
    mounted,
    source,
    successOnlyOnCompleteData,
    errors: {
      _any_error: false,
      _originPoints: [],
      _samplePoints: [],
      _scenarios: [],
      _segments: [],
      _samples: [],
    },
  };

  const tests = [];
  const segments = [];
  const samplePoints = [];
  const samples = [];
  const originPoints = [];
  const floorPlans = [];

  try {
    if (!projectId || typeof +projectId !== 'number') {
      throw new Error(`Incorrect Project ID`);
    }

    if (delayBetweenTestsMs) {
      await new Promise((res) => setTimeout(res, 5000));
    }

    const [projectData, sampleCollectors] = await Promise.all([
      getProjectData(projectId, maxAttempts, globalIndicators),
      getSampleCollectorLabels(projectId, maxAttempts, globalIndicators),
    ]);

    if (!projectData || !sampleCollectors) {
      throw new Error(`Could not get Project related data`);
    }

    const project = getProjectFromProjectData(projectData);
    if (!project) {
      throw new Error(
        `Wrong format of Project Data. Could not get Project from it.`,
      );
    }

    const { tests: projectTests } = projectData;

    if (!Array.isArray(projectTests)) {
      throw new Error(
        `Wrong project tests data format. Expected it to be an array`,
      );
    }

    const segmentsPromises = [];
    const originPointsPromises = [];
    const samplePointsPromises = [];
    const samplesPromises = [];

    for (let test of projectTests) {
      if (delayBetweenTestsMs) {
        await new Promise((res) => setTimeout(res, delayBetweenTestsMs));
      }
      const testObj = getTestFromTestData(test);
      const testId = testObj.id;

      tests.push(testObj);

      segmentsPromises.push(
        getTestSegmentData(testId, maxAttempts, globalIndicators),
      );
      originPointsPromises.push(
        getOriginPointData(testId, maxAttempts, globalIndicators),
      );

      samplePointsPromises.push(
        getSamplePointsData(testId, maxAttempts, globalIndicators),
      );

      samplesPromises.push(
        getSamplesData(testId, maxAttempts, globalIndicators),
      );
    }

    const [
      testsSegmentsMatrix,
      originPointsMatrix,
      samplePointsMatrix,
      samplesMatrix,
    ] = await Promise.all([
      Promise.all(segmentsPromises),
      Promise.all(originPointsPromises),
      Promise.all(samplePointsPromises),
      Promise.all(samplesPromises),
    ]).catch(() => {
      globalIndicators.shouldStop = true;
      return [];
    });

    if (
      !testsSegmentsMatrix ||
      !originPointsMatrix ||
      !samplePointsMatrix ||
      !samplesMatrix
    ) {
      throw new Error(`Could not get Tests related data`);
    }

    const [segmentsFailLoad, testsSegmentsData] =
      filterAndTransformMatrix(testsSegmentsMatrix);
    const [opFailLoad, originPointsData] = filterAndTransformMatrix(
      originPointsMatrix,
      false,
    );
    const [spFailLoad, samplePointsData] =
      filterAndTransformMatrix(samplePointsMatrix);
    const [samplesFailLoad, samplesData] =
      filterAndTransformMatrix(samplesMatrix);

    if (
      successOnlyOnCompleteData &&
      (segmentsFailLoad || opFailLoad || spFailLoad || samplesFailLoad)
    ) {
      throw new Error();
    }

    segments.push(...testsSegmentsData);
    samples.push(...samplesData);
    samplePoints.push(...samplePointsData);

    const floorPlansFileNames = [];

    for (let {
      originPoints: originPointsOneTest,
      floorPlans: floorPlansFromOPs,
    } of originPointsData) {
      originPoints.push(...originPointsOneTest);

      for (let { fileName, heatmapVersion } of floorPlansFromOPs) {
        if (!~floorPlansFileNames.indexOf(fileName)) {
          floorPlans.push({ fileName, heatmapVersion });
          floorPlansFileNames.push(fileName);
        }
      }
    }

    const scenariosPromises = [];
    for (let { id, testId } of segments) {
      if (delayBetweenTestsMs) {
        await new Promise((res) => setTimeout(res, delayBetweenTestsMs));
      }
      scenariosPromises.push(
        getScenarioData(id, testId, maxAttempts, globalIndicators),
      );
    }
    const scenariosMatrix = await Promise.all(scenariosPromises).catch(() => {
      globalIndicators.shouldStop = true;
      return [];
    });

    if (!Array.isArray(scenariosMatrix)) {
      throw new Error(
        `Wrong scenarios data format. Expected it to be an array`,
      );
    }

    const [scenariosFailLoad, scenarios] =
      filterAndTransformMatrix(scenariosMatrix);
    if (successOnlyOnCompleteData && scenariosFailLoad) {
      throw new Error();
    }

    const _floorplans = await Promise.all(
      floorPlans.map(({ fileName, heatmapVersion }) =>
        downloadFloorPlan(
          projectId,
          fileName,
          heatmapVersion,
          maxAttempts,
          globalIndicators,
        ),
      ),
    ).catch(() => {
      globalIndicators.shouldStop = true;
      return [];
    });
    const _testFloorPlans = await Promise.all(
      tests.map(async (t) => ({
        testId: t.id,
        floorPlanData: await downloadFloorPlanByTestId(
          t.id,
          maxAttempts,
          globalIndicators,
        ),
      })),
    ).catch(() => {
      globalIndicators.shouldStop = true;
      return [];
    });

    return {
      _floorplans,
      _tests: tests,
      _testFloorPlans,
      _sampleCollectors: sampleCollectors,
      _project: project,
      _originPoints: originPoints,
      _samplePoints: samplePoints,
      _scenarios: scenarios,
      _segments: segments,
      _samples: samples,
    };
  } catch (err) {
    globalIndicators.shouldStop = true;
    console.error(`getDataForProjectConfiguration Error: `, err);
    return null;
  }
};

/**
 * Filters non-null elements and flatens the initial matrix.
 * Returns an array with 2 piece of data: information about presence of empty elements in the initial matrix
 * and new flatened array or new matrix with non-null elements.
 * @param {Array<[] | {} | null>} matrix
 * @param {boolean} shouldFlaten
 * @returns {[boolean, Array<[] | {}>]}
 */
const filterAndTransformMatrix = (matrix, shouldFlaten = true) => {
  if (!Array.isArray(matrix)) {
    return [];
  }
  const _arr = [];
  let emptyElements = false;

  for (let el of matrix) {
    if (!el) {
      emptyElements = emptyElements || true;
      continue;
    }

    if (shouldFlaten) {
      if (Array.isArray(el)) {
        _arr.push(...el);
      }
    } else {
      _arr.push(el);
    }
  }

  return [emptyElements, _arr];
};

const getProjectData = async (projectId, maxAttempts, globalObj = {}) => {
  try {
    const response = await projectsApi.getOneProject(
      projectId,
      false,
      globalObj.source,
    );

    if (isValidResponse(response)) {
      return getDataFromResponse(response);
    } else throw new Error(getErrorMessageFromResponse(response));
  } catch (err) {
    if (globalObj.shouldStop) {
      return Promise.resolve();
    } else if (maxAttempts > 0) {
      return await getProjectData(projectId, --maxAttempts, globalObj);
    } else {
      //console.error(`getProjectData Error: `, err);
      return null;
    }
  }
};

const getSampleCollectorLabels = async (
  projectId,
  maxAttempts,
  globalObj = {},
) => {
  try {
    if (!shouldContinueRequests(globalObj)) {
      return globalObj.successOnlyOnCompleteData
        ? Promise.reject()
        : Promise.resolve();
    }
    const response = await samplePlansApi.getLabelsForProject(
      projectId,
      globalObj.source,
    );
    if (isValidResponse(response)) {
      const data = getDataFromResponse(response);

      if (!Array.isArray(data)) {
        throw new Error(
          `Wrong sample collector labels data from server. Expected it to be an array`,
        );
      }

      const result = [];

      for (let {
        sampleNumber,
        SPNumber,
        plates,
        projectName,
        qrcode,
        sampleType,
        surveyDate,
      } of data) {
        result.push({
          sampleNumber,
          plates,
          projectName,
          sampleType,
          surveyDate,
          qrCode: qrcode,
          spNumber: SPNumber,
        });
      }

      return result;
    } else throw new Error(getErrorMessageFromResponse(response));
  } catch (err) {
    if (globalObj.shouldStop) {
      return Promise.resolve();
    } else if (maxAttempts > 0) {
      return await getSampleCollectorLabels(
        projectId,
        --maxAttempts,
        globalObj,
      );
    }

    //console.error(`getSampleCollectorLabels Error: `, err);

    return Promise.reject(err);
  }
};

const getTestSegmentData = async (testId, maxAttempts, globalObj = {}) => {
  try {
    if (!shouldContinueRequests(globalObj)) {
      return globalObj.successOnlyOnCompleteData
        ? Promise.reject()
        : Promise.resolve();
    }
    const response = await segmentsApi.getSegments(testId, globalObj.source);

    if (isValidResponse(response)) {
      const segmentsData = getDataFromResponse(response);

      if (!Array.isArray(segmentsData)) {
        throw new Error(
          `Wrong data format. Expected segments data to be an array`,
        );
      }

      const segments = segmentsData.map((segment) =>
        getSegmentFromSegmentsData(segment),
      );

      return segments;
    } else throw new Error(getErrorMessageFromResponse(response));
  } catch (err) {
    if (!shouldContinueRequests(globalObj)) {
      return globalObj.successOnlyOnCompleteData
        ? Promise.reject()
        : Promise.resolve();
    }
    if (maxAttempts > 0) {
      return await getTestSegmentData(testId, --maxAttempts, globalObj);
    } else {
      addErrorToGlobalObj(globalObj, testId, '_segments');
      return null;
    }
  }
};

const shouldContinueRequests = (globalObj) => {
  if (!globalObj) return true;

  if (globalObj.mounted && !globalObj.mounted.current) {
    return false;
  }

  const { shouldStop, successOnlyOnCompleteData } = globalObj;
  const anyReqError = globalObj.errors?._any_error;

  return (
    !shouldStop &&
    (!successOnlyOnCompleteData || (successOnlyOnCompleteData && !anyReqError))
  );
};

const addErrorToGlobalObj = (globalObj, entityId, key) => {
  if (!!globalObj?.errors) {
    globalObj.errors._any_error = true;
  }
  if (
    Array.isArray(globalObj?.errors?.[key]) &&
    !globalObj?.errors?.[key].includes(entityId)
  ) {
    globalObj?.errors?.[key].push(entityId);
  }
};

const getScenarioData = async (
  segmentId,
  testId,
  maxAttempts,
  globalObj = {},
) => {
  try {
    if (!shouldContinueRequests(globalObj)) {
      return globalObj.successOnlyOnCompleteData
        ? Promise.reject()
        : Promise.resolve();
    }

    const response = await scenariosApi.getScenarios(
      segmentId,
      globalObj.source,
    );

    if (isValidResponse(response)) {
      const scenariosData = getDataFromResponse(response);

      if (!Array.isArray(scenariosData)) {
        throw new Error(
          `Wrong data format. Expected scenarios data to be an array`,
        );
      }

      const scenarios = scenariosData.map((scenario) =>
        getScenarioFromScenarioData(scenario, testId),
      );

      return scenarios;
    } else throw new Error(getErrorMessageFromResponse(response));
  } catch (err) {
    if (!shouldContinueRequests(globalObj)) {
      return globalObj.successOnlyOnCompleteData
        ? Promise.reject()
        : Promise.resolve();
    }
    if (maxAttempts > 0) {
      return await getScenarioData(segmentId, testId, --maxAttempts, globalObj);
    } else {
      addErrorToGlobalObj(globalObj, testId, '_scenarios');
      return null;
    }
  }
};

const getSamplePointsData = async (testId, maxAttempts, globalObj = {}) => {
  try {
    if (!shouldContinueRequests(globalObj)) {
      return globalObj.successOnlyOnCompleteData
        ? Promise.reject()
        : Promise.resolve();
    }

    const response = await samplePointsApi.getAllSamplePointsForTest(
      testId,
      globalObj.source,
    );

    if (isValidResponse(response)) {
      const samplePointsData = getDataFromResponse(response);

      if (!Array.isArray(samplePointsData)) {
        throw new Error(
          `Wrong data format. Expected sample points data to be an array`,
        );
      }

      const samplePoints = samplePointsData.map((spData) =>
        getSamplePointsFromSPData(spData, testId),
      );

      return samplePoints;
    } else throw new Error(getErrorMessageFromResponse(response));
  } catch (err) {
    if (!shouldContinueRequests(globalObj)) {
      return globalObj.successOnlyOnCompleteData
        ? Promise.reject()
        : Promise.resolve();
    }
    if (maxAttempts > 0) {
      return await getSamplePointsData(testId, --maxAttempts, globalObj);
    } else {
      addErrorToGlobalObj(globalObj, testId, '_samplePoints');
      return null;
    }
  }
};

const getSamplesData = async (testId, maxAttempts, globalObj = {}) => {
  try {
    if (!shouldContinueRequests(globalObj)) {
      return globalObj.successOnlyOnCompleteData
        ? Promise.reject()
        : Promise.resolve();
    }

    const response = await samplePointsApi.getAllSamples(
      testId,
      null,
      globalObj.source,
    );

    if (isValidResponse(response)) {
      const samplesData = getDataFromResponse(response);

      if (!Array.isArray(samplesData)) {
        throw new Error(
          `Wrong data format. Expected samples data to be an array`,
        );
      }

      const samples = samplesData.map((sampleData) =>
        getSampleFromSampleData(sampleData, testId),
      );

      return samples;
    } else throw new Error(getErrorMessageFromResponse(response));
  } catch (err) {
    if (!shouldContinueRequests(globalObj)) {
      return globalObj.successOnlyOnCompleteData
        ? Promise.reject()
        : Promise.resolve();
    }
    if (maxAttempts > 0) {
      return await getSamplesData(testId, --maxAttempts, globalObj);
    } else {
      addErrorToGlobalObj(globalObj, testId, '_samples');
      return null;
    }
  }
};

const getOriginPointData = async (testId, maxAttempts, globalObj = {}) => {
  try {
    if (!shouldContinueRequests(globalObj)) {
      return globalObj.successOnlyOnCompleteData
        ? Promise.reject()
        : Promise.resolve();
    }

    const response = await originPointsApi.getAllOriginPointsForTest(
      testId,
      globalObj.source,
    );

    if (isValidResponse(response)) {
      const originPointsData = getDataFromResponse(response);

      if (!Array.isArray(originPointsData)) {
        throw new Error(
          `Wrong data format. Expected origin points data to be an array`,
        );
      }

      const originPoints = [];

      const floorPlans = [];
      const fpFileNames = [];

      for (let opData of originPointsData) {
        originPoints.push(getOriginPointFromOPData(opData, testId));

        if (!~fpFileNames.indexOf(opData.floorplan)) {
          floorPlans.push({
            fileName: opData.floorplan,
            heatmapVersion: opData.heatmapversion,
          });

          fpFileNames.push(opData.floorplan);
        }
      }

      return { originPoints, floorPlans };
    } else throw new Error(getErrorMessageFromResponse(response));
  } catch (err) {
    if (!shouldContinueRequests(globalObj)) {
      return globalObj.successOnlyOnCompleteData
        ? Promise.reject()
        : Promise.resolve();
    }
    if (maxAttempts > 0) {
      return await getOriginPointData(testId, --maxAttempts, globalObj);
    } else {
      addErrorToGlobalObj(globalObj, testId, '_originPoints');
      return null;
    }
  }
};

const downloadFloorPlan = async (
  projectId,
  fileName,
  heatmapVersion,
  maxAttempts,
  globalObj,
) => {
  try {
    if (!fileName) {
      return {
        fileName: null,
        url: null,
      };
    }
    if (!shouldContinueRequests(globalObj)) {
      return globalObj.successOnlyOnCompleteData
        ? Promise.reject()
        : Promise.resolve();
    }
    const newAxiosInstance = axios.create();

    const response = await floorPlansApi.getPresignedURL(
      {
        projectId,
        filename: fileName,
        action: 'getObject',
        heatmapVersion: 1,
      },
      globalObj.source,
    );

    const preSignedURL = response?.data?.preSignedURL;

    if (!preSignedURL) {
      throw new Error(`No preSignedURL`);
    }

    const _response = await newAxiosInstance.get(preSignedURL, {
      responseType: 'blob',
    });

    const data = _response?.data;
    const type = _response?.data?.type;

    if (!type || !data) {
      throw new Error(`No type of file or file content`);
    }

    const blob = new Blob([data], {
      type,
    });

    const createObjectURL =
      window.URL.createObjectURL || window.webkitURL.createObjectURL;

    const url = createObjectURL(blob);

    if (!url) {
      throw new Error();
    }

    return {
      fileName,
      url,
      heatmapVersion,
    };
  } catch (err) {
    if (globalObj.shouldStop) {
      return Promise.resolve();
    } else if (maxAttempts > 0) {
      return await downloadFloorPlan(
        projectId,
        fileName,
        heatmapVersion,
        --maxAttempts,
        globalObj,
      );
    } else {
      //console.error(`downloadFloorPlan Error: `, err);
      return null;
    }
  }
};

const downloadFloorPlanByTestId = async (testId, maxAttempts, globalObj) => {
  try {
    if (!shouldContinueRequests(globalObj)) {
      return globalObj.successOnlyOnCompleteData
        ? Promise.reject()
        : Promise.resolve();
    }
    const response = await floorPlansApi.getFloorPlanImageByTestId(
      testId,
      globalObj.source,
    );
    if (response.status === 200 && response.data) {
      const metadata = response.data.metadata;

      let finalUrl = response.data.preSignedURL;
      if (metadata && metadata.cropData) {
        finalUrl = await cropImageFromUrlPromisified({
          url: finalUrl,
          cropData: metadata.cropData,
        });
      }

      if (!finalUrl) {
        throw new Error();
      }

      return {
        url: finalUrl,
      };
    } else throw new Error(getErrorMessageFromResponse(response));
  } catch (e) {
    if (globalObj.shouldStop) {
      return Promise.resolve();
    } else if (maxAttempts > 0) {
      return await downloadFloorPlanByTestId(testId, --maxAttempts, globalObj);
    } else {
      return null;
    }
  }
};

export default getDataForProjectConfiguration;
